import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Edit, Trash2, Clock, Flame, Star, Award } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DietIcon } from '@/components/ui/DietIcon';
import { ReorderControls, CardLoadingOverlay } from '@/components/ui/ReorderControls';
import { formatCurrency } from '@/lib/utils';
import { SPICE_LEVELS, ALLERGEN_TYPES, CUISINE_TYPES } from '@/types';
import type { MenuItem } from '@/types';

interface MenuItemCardProps {
  item: MenuItem;
  isSelected?: boolean;
  onEdit: (item: MenuItem) => void;
  onDelete: (item: MenuItem) => void;
  onToggleAvailability: (item: MenuItem) => void;
  onToggleSelection?: (item: MenuItem) => void;
  showSelection?: boolean;
  // New props for reordering
  allItems?: MenuItem[];
  onReorderItems?: (items: MenuItem[]) => void;
  isReordering?: boolean; // This specific card is being reordered
  isAnyReordering?: boolean; // Any card is being reordered
}

/**
 * MenuItemCard - Comprehensive menu item display card
 * Shows all item details with clean organization
 */
const MenuItemCard: React.FC<MenuItemCardProps> = memo(({
  item,
  isSelected = false,
  onEdit,
  onDelete,
  onToggleAvailability,
  onToggleSelection,
  showSelection = false,
  allItems,
  onReorderItems,
  isReordering = false,
  isAnyReordering = false,
}) => {
  // Use enhanced spice level if available, otherwise map old numeric system
  // Only show spice info if there's a meaningful spice level (not 'none' or undefined)
  const spiceInfo = (() => {
    // Check enhanced spice level first
    if (item.enhancedSpiceLevel?.level && 
        item.enhancedSpiceLevel.level !== 'none' && 
        item.enhancedSpiceLevel.level.trim() !== '') {
      return SPICE_LEVELS[item.enhancedSpiceLevel.level];
    }
    
    // Fallback to old numeric system
    if (item.spiceLevel && item.spiceLevel > 0) {
      const spiceLevelMap = {
        1: 'mild',
        2: 'mild', 
        3: 'medium',
        4: 'hot',
        5: 'extra-hot'
      } as const;
      return SPICE_LEVELS[spiceLevelMap[item.spiceLevel as keyof typeof spiceLevelMap]];
    }
    
    return null;
  })();

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      onDelete(item);
    }
  };

  const handleSelectionToggle = () => {
    if (onToggleSelection) {
      onToggleSelection(item);
    }
  };

  // Check if item has enhanced allergen data
  const hasEnhancedAllergens = item.enhancedAllergens && (
    Object.keys(ALLERGEN_TYPES).some(key => 
      item.enhancedAllergens![key as keyof typeof ALLERGEN_TYPES]
    ) || 
    item.enhancedAllergens.custom.length > 0 ||
    item.enhancedAllergens.mayContain.length > 0
  );

  return (
    <div
      className={`
        relative bg-white rounded-xl border transition-all duration-200 overflow-hidden
        ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-300'}
        ${!item.isAvailable ? 'opacity-60' : ''}
        ${showSelection ? 'cursor-pointer' : ''}
        ${showSelection && !isSelected ? 'hover:scale-[1.01]' : ''}
      `}
      onClick={showSelection ? handleSelectionToggle : undefined}
    >
      {/* Loading Overlay */}
      <CardLoadingOverlay loading={isReordering} />
      
      {/* Selection indicator */}
      {showSelection && (
        <div className={`
          absolute top-3 left-3 w-5 h-5 rounded-full border-2 z-10 transition-colors
          ${isSelected 
            ? 'bg-primary/100 border-primary' 
            : 'bg-white border-gray-300 hover:border-gray-400'
          }
        `}>
          {isSelected && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-full h-full flex items-center justify-center text-white text-xs"
            >
              ✓
            </motion.div>
          )}
        </div>
      )}

      {/* Availability toggle */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleAvailability(item);
        }}
        className={`
          absolute bottom-3 left-3 px-2 py-1 rounded-full text-xs font-medium transition-colors z-10 shadow-sm border
          ${item.isAvailable 
            ? 'bg-green-100 text-green-800 hover:bg-green-200 border-green-200' 
            : 'bg-red-100 text-red-800 hover:bg-red-200 border-red-200'
          }
        `}
      >
        {item.isAvailable ? 'Available' : 'Unavailable'}
      </button>

      <div className="p-4 pb-16">
        {/* Main header section */}
        <div className="flex items-start space-x-3 mb-3">
          {/* Item Image */}
          {item.imageUrl ? (
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ) : (
            <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}

          {/* Item Details - match image height and center content */}
          <div className="flex-1 h-16 flex flex-col justify-center">
            {/* Title and price row - centered vertically to image */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-1">
                <h3 className="font-medium text-gray-900">{item.name}</h3>
                {/* Diet type indicator */}
                <DietIcon 
                  type={item.dietType === 'contains-eggs' ? 'eggs' : item.dietType} 
                  size={16}
                />
              </div>
              <span className="font-semibold text-primary ml-4 flex-shrink-0">
                {formatCurrency(item.price)}
              </span>
            </div>
          </div>
        </div>

        {/* Description - Always left aligned */}
        {item.description && (
          <div className="mb-3">
            <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
          </div>
        )}

        {/* Additional info row */}
        <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
          {item.preparationTime && (
            <div className="flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{item.preparationTime} min</span>
            </div>
          )}
          
          {spiceInfo && (
            <div className="flex items-center space-x-1">
              <Flame className="w-3 h-3" />
              <span>{spiceInfo.label}</span>
            </div>
          )}
          
          {item.calories && (
            <span>{item.calories} kcal</span>
          )}
        </div>

        {/* Cuisine Tags and Special Badges */}
        {(item.cuisineTags && item.cuisineTags.length > 0) || item.isSignatureDish || item.isChefSpecial || item.isPopular ? (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {/* Cuisine Tags */}
              {item.cuisineTags && item.cuisineTags.map((tag, index) => {
                const displayText = [
                  CUISINE_TYPES[tag.type].label,
                  tag.region && tag.region.charAt(0).toUpperCase() + tag.region.slice(1),
                  tag.subType && tag.subType.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')
                ].filter(Boolean).join(' - ');
                
                return (
                  <span
                    key={index}
                    className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
                  >
                    {displayText}
                  </span>
                );
              })}
              
              {/* Special badges */}
              {item.isSignatureDish && (
                <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <Star className="w-3 h-3" />
                  <span>Signature</span>
                </div>
              )}
              {item.isChefSpecial && (
                <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <Award className="w-3 h-3" />
                  <span>Chef's Special</span>
                </div>
              )}
              {item.isPopular && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Popular
                </span>
              )}
            </div>
          </div>
        ) : null}

        {/* Custom Tags */}
        {item.customTags && item.customTags.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {item.customTags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Ingredients */}
        {item.ingredients && item.ingredients.length > 0 && (
          <div className="mb-2">
            <span className="text-xs font-medium text-gray-700">Ingredients: </span>
            <span className="text-xs text-gray-600">
              {item.ingredients.join(', ')}
            </span>
          </div>
        )}
        
        {/* Enhanced Allergens Display */}
        {hasEnhancedAllergens && item.enhancedAllergens && (
          <div className="space-y-1 mb-3">
            <div>
              <span className="text-xs font-medium text-gray-700">Contains: </span>
              <span className="text-xs text-red-600">
                {[
                  ...Object.entries(ALLERGEN_TYPES)
                    .filter(([key]) => item.enhancedAllergens![key as keyof typeof ALLERGEN_TYPES])
                    .map(([, value]) => value.label),
                  ...item.enhancedAllergens.custom
                ].join(', ')}
              </span>
            </div>
            {item.enhancedAllergens.mayContain.length > 0 && (
              <div>
                <span className="text-xs font-medium text-gray-700">May contain: </span>
                <span className="text-xs text-yellow-600">
                  {item.enhancedAllergens.mayContain.join(', ')}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Fallback to basic allergens if no enhanced data */}
        {!hasEnhancedAllergens && item.allergens && item.allergens.length > 0 && (
          <div className="mb-3">
            <span className="text-xs font-medium text-gray-700">Contains: </span>
            <span className="text-xs text-red-600">
              {item.allergens.join(', ')}
            </span>
          </div>
        )}

        {/* Enhanced spice notes */}
        {item.enhancedSpiceLevel?.preparationNotes && (
          <div className="mb-3 text-xs text-gray-600">
            <span className="font-medium">Spice Note: </span>
            <span>{item.enhancedSpiceLevel.preparationNotes}</span>
          </div>
        )}

        {/* Can be prepared section */}
        {(item.isVegan || item.isJain) && (
          <div className="mb-3 flex items-center gap-3 text-xs text-gray-600">
            {item.isVegan && (
              <div className="flex items-center gap-1">
                <DietIcon type="vegan" size={14} />
                <span>Can be prepared vegan</span>
              </div>
            )}
            {item.isJain && (
              <div className="flex items-center gap-1">
                <DietIcon type="jain" size={14} />
                <span>Can be prepared Jain</span>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        {!showSelection && (
          <div className="flex items-center justify-end space-x-2 pt-3 absolute bottom-3 right-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
              className="p-2"
              title="Edit Item"
            >
              <Edit className="w-4 h-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete Item"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            
            {/* Reorder Controls */}
            {allItems && onReorderItems && (
              <ReorderControls
                item={item}
                items={allItems}
                onReorder={onReorderItems}
                getItemId={(item) => item.id}
                isAnyReordering={isAnyReordering}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
});

MenuItemCard.displayName = 'MenuItemCard';

export { MenuItemCard };